import type { ReactNode } from 'react'

import { COMMON } from '@brickup/shared/locales/de/common'
import { Button } from './Button'
import { useEditorialSurface } from './theme-scope'
import { CtaButton } from '@/components/CtaButton'

/**
 * A dialog's two answers: leave it, or do the thing.
 *
 * Written out at every dialog, the pair drifted — the label, the order, which
 * of the two carried the pending state. Here the order is fixed (cancel left,
 * the action right, because the action is where the eye ends up) and the
 * labels default to the words every other dialog uses.
 *
 * The buttons follow the area: navy `Button` on Internal, editorial
 * `CtaButton` on Customer and Public, read from the scope those two are
 * wrapped in. A dialog shown on both — the account dialog is — therefore
 * needs no prop to say where it is.
 *
 * `formId` is for a body that is a `<form>`: the button submits it rather than
 * calling a handler, so the browser's own validation and the Enter key keep
 * working. A footer with neither `onSubmit` nor `formId` is close-only.
 */
export function ModalFooter({
  onCancel,
  onSubmit,
  onDelete,
  deletePending,
  formId,
  submitLabel = COMMON.save,
  cancelLabel = COMMON.cancel,
  submitDisabled,
  isPending,
  destructive = false,
  leading,
}: {
  onCancel: () => void
  /** Omitted where `formId` carries the press, or where there is no action. */
  onSubmit?: () => void
  /**
   * Renders a delete button hard left. A delete beside a save is one mis-click
   * away from the thing it cannot undo.
   */
  onDelete?: () => void
  deletePending?: boolean
  formId?: string
  submitLabel?: string
  cancelLabel?: string
  submitDisabled?: boolean
  isPending?: boolean
  /** The action cannot be undone — it is drawn as such. */
  destructive?: boolean
  /** Anything else that belongs at the far left: a count, a hint, an action. */
  leading?: ReactNode
}) {
  const editorial = useEditorialSurface()
  const hasSubmit = onSubmit !== undefined || formId !== undefined

  const cancel = editorial ? (
    <CtaButton
      tone="ghost"
      icon={null}
      label={cancelLabel}
      onClick={onCancel}
      layout="hug"
    />
  ) : (
    <Button reads variant="outline" onClick={onCancel}>
      {cancelLabel}
    </Button>
  )

  const submit = editorial ? (
    <CtaButton
      tone={destructive ? 'danger' : 'solid'}
      icon={null}
      label={submitLabel}
      onClick={onSubmit}
      submitsForm={formId}
      disabled={submitDisabled}
      loading={isPending}
      layout="hug"
    />
  ) : (
    <Button
      form={formId}
      type={formId ? 'submit' : 'button'}
      variant={destructive ? 'destructive' : 'default'}
      disabled={submitDisabled}
      isLoading={isPending}
      onClick={onSubmit}
    >
      {submitLabel}
    </Button>
  )

  const remove = editorial ? (
    <CtaButton
      tone="danger"
      icon={null}
      label={COMMON.delete}
      onClick={onDelete}
      loading={deletePending}
      layout="hug"
    />
  ) : (
    <Button
      variant="destructive"
      size="sm"
      isLoading={deletePending}
      onClick={onDelete}
    >
      {COMMON.delete}
    </Button>
  )

  return (
    <div className="flex w-full flex-row items-center justify-between gap-3">
      {onDelete ? remove : (leading ?? <span />)}
      <div className="flex shrink-0 flex-row gap-2">
        {cancel}
        {hasSubmit ? submit : null}
      </div>
    </div>
  )
}
